import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Trophy, User, Info, Search, History as HistoryIcon, ArrowLeft,
} from 'lucide-react'
import {
  useChallengeHistory,
  useChallenges,
  useCurrentWeekChallenge,
  useDepositToChallenge,
  useWithdrawFromChallenge,
  useResetChallenge,
  useSetupWeeklyChallenge,
} from '@/hooks/useStrategies'
import { TeamCrest } from '@/components/TeamCrest'
import { ResponsibleNotice } from '@/components/ResponsibleNotice'
import { EmptyState } from '@/components/EmptyState'
import { BankrollMovementModal } from '@/components/BankrollMovementModal'
import { ResetBankrollModal } from '@/components/ResetBankrollModal'
import { PillTabs } from '@/components/PillTabs'
import { PickDetailView } from '@/components/challenge/PickDetailView'
import { WeekView } from '@/components/challenge/WeekView'
import { HistoryView } from '@/components/challenge/HistoryView'
import { cn } from '@/lib/utils'
import type { ChallengeSummary, ChallengeHistoryItem } from '@/types/strategies'

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  ganada: { label: 'Acertó', className: 'bg-success/15 text-success border-success/40' },
  perdida: { label: 'Falló', className: 'bg-danger/15 text-danger border-danger/40' },
  void: { label: 'Anulado', className: 'bg-panel-elevated text-text-dim border-border/50' },
  pendiente: { label: 'Pendiente', className: 'bg-brand/15 text-brand border-brand/40' },
}

const PRESET_AMOUNTS = [50, 100, 200, 500]

function kickoffShortLabel(iso: string | null): string {
  if (!iso) return '—'
  return new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' })
}

function WeekSparkline({ items, tone }: { items: ChallengeHistoryItem[]; tone: 'yellow' | 'brand' }) {
  const points = items
    .filter((it) => it.bankroll_after != null)
    .sort((a, b) => (a.step_date ?? '').localeCompare(b.step_date ?? ''))
    .map((it) => it.bankroll_after as number)
  if (points.length < 2) return null
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const w = 100
  const h = 32
  const last = points[points.length - 1]
  const coords = points.map((v, i) => {
    const x = (i / (points.length - 1)) * w
    const y = h - ((v - min) / range) * h
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  return (
    <div className="relative pt-1">
      <span className={cn('absolute -top-1 right-0 text-[9px] font-mono font-black px-1.5 py-0.5 rounded-md', tone === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-brand/20 text-brand')}>
        {last.toFixed(2)}€
      </span>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-9 mt-3" preserveAspectRatio="none">
        <polyline points={coords.join(' ')} fill="none" className={tone === 'yellow' ? 'stroke-yellow-400' : 'stroke-brand'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

function PickRow({ item, onOpen }: { item: ChallengeHistoryItem; onOpen: () => void }) {
  const isNoBet = item.display_market == null
  const badge = STATUS_BADGE[item.status] ?? STATUS_BADGE.pendiente
  return (
    <button
      onClick={onOpen}
      disabled={isNoBet}
      className="w-full flex items-center gap-2.5 bg-panel-elevated/30 border border-border/30 rounded-xl px-2.5 py-2 text-left disabled:opacity-60 disabled:pointer-events-none active-scale hover:border-border/60 transition"
    >
      <span className="text-[9px] font-bold text-text-dim w-9 shrink-0">{kickoffShortLabel(item.step_date)}</span>
      {isNoBet ? (
        <span className="text-[10px] text-text-dim italic flex-1">Sin oportunidad</span>
      ) : (
        <>
          <div className="flex -space-x-1.5 shrink-0">
            <TeamCrest src={item.home_team_logo} alt={item.home_team ?? 'Local'} size={20} />
            <TeamCrest src={item.away_team_logo} alt={item.away_team ?? 'Visitante'} size={20} />
          </div>
          <span className="text-[11px] font-bold text-text truncate flex-1">
            {item.home_team ?? 'Local'} vs {item.away_team ?? 'Visitante'}
          </span>
          {item.odds != null && <span className="text-[10px] font-mono font-bold text-text-dim shrink-0">{item.odds.toFixed(2)}</span>}
          <span className={cn('text-[8px] px-1.5 py-0.5 rounded-full border font-black uppercase shrink-0', badge.className)}>
            {badge.label}
          </span>
        </>
      )}
    </button>
  )
}

function BankrollPresetPanel({ hasExisting, currentBankroll }: { hasExisting: boolean; currentBankroll: number }) {
  const [amount, setAmount] = useState<number>(hasExisting ? currentBankroll : 100)
  const setup = useSetupWeeklyChallenge()
  const deposit = useDepositToChallenge()
  const withdraw = useWithdrawFromChallenge()
  const pending = setup.isPending || deposit.isPending || withdraw.isPending

  function apply() {
    if (!hasExisting) {
      setup.mutate(amount)
      return
    }
    const delta = Math.round((amount - currentBankroll) * 100) / 100
    if (delta > 0) deposit.mutate({ amount: delta })
    else if (delta < 0) withdraw.mutate({ amount: Math.abs(delta) })
  }

  return (
    <div className="card p-4 space-y-3 border-brand/20">
      <p className="text-[10px] font-black uppercase tracking-wider text-text-dim">
        Banca {hasExisting ? 'actual' : 'inicial'} (tu reto)
      </p>
      <input
        type="number" inputMode="decimal" min={1} step="0.01" value={amount}
        onChange={(e) => setAmount(Number(e.target.value))}
        className="w-full bg-panel-elevated border border-border/60 rounded-xl px-3 py-2.5 text-base font-mono font-bold text-text text-center focus:outline-none focus:border-brand/50"
      />
      <div className="grid grid-cols-4 gap-1.5">
        {PRESET_AMOUNTS.map((p) => (
          <button
            key={p}
            onClick={() => setAmount(p)}
            className={cn(
              'py-2 rounded-lg text-[11px] font-black transition',
              amount === p ? 'bg-brand text-black' : 'bg-panel-elevated border border-border/60 text-text-dim hover:text-text',
            )}
          >
            {p}€
          </button>
        ))}
      </div>
      <button
        onClick={apply}
        disabled={pending || !(amount > 0)}
        className="w-full py-2.5 rounded-xl bg-brand text-black text-xs font-black uppercase tracking-wider disabled:opacity-40 disabled:pointer-events-none"
      >
        {hasExisting ? 'Actualizar banca' : 'Crear Mi Reto'}
      </button>
      {(setup.isError || deposit.isError || withdraw.isError) && (
        <p className="text-[10px] text-danger">No se pudo actualizar la banca. Intenta de nuevo.</p>
      )}
    </div>
  )
}

function ChallengeHome({
  challenge, isOfficial, onOpenPick, onOpenWeek,
}: {
  challenge: ChallengeSummary
  isOfficial: boolean
  onOpenPick: (item: ChallengeHistoryItem) => void
  onOpenWeek: () => void
}) {
  const { data: history } = useChallengeHistory(challenge.challenge_id)
  const items = history?.items ?? []
  const sortedDesc = [...items].sort((a, b) => (b.step_date ?? '').localeCompare(a.step_date ?? ''))
  const picksCount = items.filter((it) => it.display_market != null).length
  const resultAmount = challenge.current_bankroll - challenge.bankroll_initial
  const positive = resultAmount >= 0
  const toneClass = isOfficial ? 'text-yellow-400' : 'text-brand'
  const rentabilidad = challenge.bankroll_initial > 0 ? (resultAmount / challenge.bankroll_initial) * 100 : null

  return (
    <>
      <div className={cn('card p-4 space-y-3 border', isOfficial ? 'border-yellow-500/40 bg-gradient-to-br from-yellow-950/30 via-panel to-panel' : 'border-brand/30 bg-gradient-to-br from-brand-soft/20 via-panel to-panel')}>
        <div className="flex items-center justify-between">
          <span className={cn('text-xs font-black uppercase flex items-center gap-1.5', toneClass)}>
            {isOfficial ? <Trophy className="h-4 w-4" /> : <User className="h-4 w-4" />}
            {isOfficial ? 'Reto John Anthony' : 'Mi Reto'}
          </span>
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-panel-elevated text-brand border border-brand/30 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" /> {challenge.status === 'ACTIVE' ? 'Activo' : challenge.status}
          </span>
        </div>
        {challenge.week_start_date && challenge.week_end_date && (
          <p className="text-[10px] text-text-dim">
            Semana {new Date(`${challenge.week_start_date}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })} -{' '}
            {new Date(`${challenge.week_end_date}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2.5">
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">Banca inicial</p>
            <p className="figure text-sm font-bold text-text">{challenge.bankroll_initial.toFixed(2)}€</p>
          </div>
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">Banca actual</p>
            <p className={cn('figure text-sm font-bold', toneClass)}>{challenge.current_bankroll.toFixed(2)}€</p>
          </div>
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">{isOfficial ? 'Resultado semanal' : 'Rentabilidad'}</p>
            <p className={cn('figure text-sm font-bold', positive ? 'text-success' : 'text-danger')}>
              {isOfficial
                ? `${positive ? '+' : ''}${resultAmount.toFixed(2)}€`
                : rentabilidad != null ? `${positive ? '+' : ''}${rentabilidad.toFixed(2)}%` : '—'}
            </p>
          </div>
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">Picks realizados</p>
            <p className="figure text-sm font-bold text-text">{picksCount} / {items.length || '—'}</p>
          </div>
        </div>

        {items.length >= 2 && <WeekSparkline items={items} tone={isOfficial ? 'yellow' : 'brand'} />}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <p className="text-[10px] font-black uppercase tracking-wider text-text-dim">Picks de la semana</p>
          {items.length > 5 && (
            <button onClick={onOpenWeek} className="text-[10px] font-bold text-cyan">Ver todos →</button>
          )}
        </div>
        {sortedDesc.length === 0 ? (
          <p className="text-[11px] text-text-dim text-center py-4">Sin picks todavía esta semana.</p>
        ) : (
          <div className="space-y-1.5">
            {sortedDesc.slice(0, 5).map((it) => (
              <PickRow key={it.step_id} item={it} onOpen={() => onOpenPick(it)} />
            ))}
          </div>
        )}
      </div>

      <button
        onClick={onOpenWeek}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-brand text-black text-xs font-black uppercase tracking-wider transition active-scale"
      >
        <Search className="h-3.5 w-3.5" /> Ver análisis completo de la semana
      </button>
    </>
  )
}

/** P1.9F (25/09/2026, referencia visual aprobada) -- rediseño 1:1 del mockup "Reto": tabs
 * Reto Oficial/Mi Reto en la cabecera (antes secciones apiladas), card semanal, picks de
 * la semana clicables (→ `PickDetailView`), "Ver análisis completo" (→ `WeekView`, con
 * evolución diaria en tabla), e "Historial de Retos" con semanas clicables. Conserva TODA
 * la lógica real: mismo `ChallengeEngine`/`ChallengeSelector`/staking, mismo pick
 * Oficial/Personal, límites de riesgo y máximo 1 pick/día intactos -- solo cambia cómo se
 * presenta, nunca cómo se calcula. */
export function ChallengePage() {
  const { data: challenges, isLoading } = useChallenges()
  const officials = challenges?.filter((c) => c.is_official) ?? []
  const official = officials.find((c) => c.status === 'ACTIVE') ?? officials[0] ?? null
  const mine = challenges?.filter((c) => !c.is_official && !c.is_admin_test) ?? []
  // Fase R.11 (Bloque D/E, 17/08/2026): el Mi Reto "actual" es el de la semana que cubre hoy,
  // vía `/challenge/current-week` -- nunca derivado de `useChallenges()` filtrando por status,
  // que puede mostrar una semana pasada ya COMPLETED/FAILED como si fuera "la de ahora"
  // (mismo bug real ya corregido en `ChallengeHomeCard.tsx`).
  const { data: currentWeek } = useCurrentWeekChallenge()
  const currentMine = currentWeek?.challenge ?? null

  const [tab, setTab] = useState<'oficial' | 'mio'>('oficial')
  type View =
    | { kind: 'home' }
    | { kind: 'week'; challengeId: number; isOfficial: boolean }
    | { kind: 'pick'; item: ChallengeHistoryItem; matchLabel: string }
    | { kind: 'history' }
  const [view, setView] = useState<View>({ kind: 'home' })

  const [modal, setModal] = useState<'deposit' | 'withdraw' | 'reset' | null>(null)
  const deposit = useDepositToChallenge()
  const withdraw = useWithdrawFromChallenge()
  const reset = useResetChallenge()
  const [showInfo, setShowInfo] = useState(false)

  const activeChallenge = tab === 'oficial' ? official : currentMine

  if (view.kind === 'pick') {
    return (
      <PickDetailView
        item={view.item}
        matchLabel={view.matchLabel}
        onBack={() => setView({ kind: 'home' })}
      />
    )
  }

  if (view.kind === 'week') {
    const siblings = view.isOfficial ? officials : mine
    const weekChallenge = siblings.find((c) => c.challenge_id === view.challengeId)
    if (!weekChallenge) {
      setView({ kind: 'home' })
      return null
    }
    return (
      <WeekView
        challenge={weekChallenge}
        isOfficial={view.isOfficial}
        siblings={siblings}
        onBack={() => setView({ kind: 'home' })}
        onSelectChallenge={(id) => setView({ kind: 'week', challengeId: id, isOfficial: view.isOfficial })}
        onOpenPick={(item) => setView({
          kind: 'pick', item,
          matchLabel: item.home_team || item.away_team ? `${item.home_team ?? 'Local'} vs ${item.away_team ?? 'Visitante'}` : (item.display_market ?? weekChallenge.name),
        })}
      />
    )
  }

  if (view.kind === 'history') {
    return (
      <HistoryView
        officials={officials}
        mine={mine}
        onBack={() => setView({ kind: 'home' })}
        onOpenWeek={(challengeId, isOfficial) => setView({ kind: 'week', challengeId, isOfficial })}
      />
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start gap-3">
        <Link to="/mas" className="p-2 rounded-xl bg-panel-elevated hover:bg-border text-text transition shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-black text-text flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-400" /> Reto
            </h1>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => setView({ kind: 'history' })} className="p-2 rounded-xl bg-panel-elevated text-text-dim hover:text-text transition" aria-label="Historial de Retos">
                <HistoryIcon className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => setShowInfo((v) => !v)} className="p-2 rounded-xl bg-panel-elevated text-text-dim hover:text-text transition" aria-label="Más info">
                <Info className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <p className="text-[10px] text-text-dim uppercase tracking-wider font-bold">John Anthony</p>
        </div>
      </div>

      {showInfo && (
        <div className="card p-3 space-y-1.5 border-border/60 bg-panel-elevated/30">
          <p className="text-xs text-text-muted leading-relaxed">
            <strong className="text-text">Máximo una selección oficial por día.</strong> John Anthony busca oportunidades con
            alta probabilidad estimada y riesgo controlado. Si no encuentra una que cumpla los criterios, espera -- no apuesta
            por obligación. El Reto Oficial y Mi Reto reciben SIEMPRE el mismo pick -- solo cambia cuánto arriesga cada uno
            según su propia banca.
          </p>
        </div>
      )}

      <PillTabs
        items={[
          { key: 'oficial', label: 'Reto Oficial' },
          { key: 'mio', label: 'Mi Reto' },
        ]}
        active={tab}
        onChange={(key) => setTab(key as 'oficial' | 'mio')}
      />

      {isLoading && <p className="text-sm text-text-dim">Cargando…</p>}

      {tab === 'oficial' && (
        official ? (
          <ChallengeHome
            challenge={official}
            isOfficial
            onOpenPick={(item) => setView({
              kind: 'pick', item,
              matchLabel: item.home_team || item.away_team ? `${item.home_team ?? 'Local'} vs ${item.away_team ?? 'Visitante'}` : (item.display_market ?? official.name),
            })}
            onOpenWeek={() => setView({ kind: 'week', challengeId: official.challenge_id, isOfficial: true })}
          />
        ) : (
          !isLoading && <EmptyState icon={Trophy} title="Sin Reto Oficial esta semana" body="Se crea automáticamente en cuanto haya una oportunidad válida." />
        )
      )}

      {tab === 'mio' && (
        <>
          <BankrollPresetPanel hasExisting={Boolean(currentMine)} currentBankroll={currentMine?.current_bankroll ?? 0} />
          {currentMine && (
            <>
              <ChallengeHome
                challenge={currentMine}
                isOfficial={false}
                onOpenPick={(item) => setView({
                  kind: 'pick', item,
                  matchLabel: item.home_team || item.away_team ? `${item.home_team ?? 'Local'} vs ${item.away_team ?? 'Visitante'}` : (item.display_market ?? currentMine.name),
                })}
                onOpenWeek={() => setView({ kind: 'week', challengeId: currentMine.challenge_id, isOfficial: false })}
              />
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setModal('withdraw')} className="py-2.5 rounded-xl bg-panel-elevated border border-border/60 text-text-dim hover:text-brand text-[10px] font-black uppercase transition">
                  Retirar
                </button>
                <button onClick={() => setModal('reset')} className="py-2.5 rounded-xl bg-panel-elevated border border-border/60 text-text-dim hover:text-warn text-[10px] font-black uppercase transition">
                  Reiniciar
                </button>
              </div>
            </>
          )}
        </>
      )}

      <ResponsibleNotice />

      {activeChallenge && (modal === 'deposit' || modal === 'withdraw') && (
        <BankrollMovementModal
          mode={modal}
          onClose={() => { setModal(null); deposit.reset(); withdraw.reset() }}
          currentBalance={activeChallenge.current_bankroll}
          isPending={modal === 'deposit' ? deposit.isPending : withdraw.isPending}
          isError={modal === 'deposit' ? deposit.isError : withdraw.isError}
          isSuccess={modal === 'deposit' ? deposit.isSuccess : withdraw.isSuccess}
          explainer="Esto registra dinero que añades o retiras de la banca de Mi Reto dentro de John Anthony. No mueve dinero bancario real."
          onSubmit={(value) => (modal === 'deposit' ? deposit.mutate({ amount: value }) : withdraw.mutate({ amount: value }))}
        />
      )}

      {activeChallenge && modal === 'reset' && (
        <ResetBankrollModal
          onClose={() => { setModal(null); reset.reset() }}
          isPending={reset.isPending}
          isError={reset.isError}
          entityLabel="Mi Reto"
          onConfirm={(reason) => reset.mutate(reason || undefined, { onSuccess: () => setModal(null) })}
        />
      )}
    </div>
  )
}
